import { useEffect, useRef } from 'react';

interface UseAuditedStatementTableLogicParams {
    hasData: boolean;
    /** False while the caller renders its loading state instead of the table — both effects
     *  re-run when it flips true, since the refs only attach once the table is in the DOM. */
    tableVisible: boolean;
    valueHeadersLength: number;
    unit: string;
    onHeaderCellWidthsChange?: (widths: number[]) => void;
    onHorizontalScroll?: (scrollLeft: number) => void;
}

/**
 * Non-render logic for AuditedStatementTable (RECUR-27656): the scroll-to-latest-FY effect and
 * the sticky-header-clone measurement (real thead cell widths + horizontal scroll position).
 * `onHeaderCellWidthsChange`/`onHorizontalScroll` are read from a ref kept current on every
 * render, so the measurement effect's deps stay [hasData, tableVisible, valueHeadersLength, unit]
 * without a stale closure or an eslint-disable.
 */
export function useAuditedStatementTableLogic({
    hasData,
    tableVisible,
    valueHeadersLength,
    unit,
    onHeaderCellWidthsChange,
    onHorizontalScroll,
}: UseAuditedStatementTableLogicParams) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const theadRowRef = useRef<HTMLTableRowElement | null>(null);

    // UAT RECUR-26713: on first render (and whenever the column set changes, e.g. a
    // Consolidated↔Standalone switch), scroll the statement horizontally to the most recent
    // (rightmost) year so the latest FY is in view on open rather than the oldest. The sticky
    // first column keeps the classification labels visible. rAF lets layout settle before we
    // read scrollWidth.
    useEffect(() => {
        const el = containerRef.current;
        if (!hasData || !tableVisible || !el) return undefined;
        const scrollToLatest = () => {
            el.scrollLeft = el.scrollWidth;
        };
        const raf = requestAnimationFrame(scrollToLatest); // after first paint
        const t = setTimeout(scrollToLatest, 250); // re-apply after late reflow (webfont + provisional icon widen the last column)
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t);
        };
    }, [hasData, tableVisible, valueHeadersLength]);

    // Ref kept in sync with the latest callbacks on every render, so `measure` below never
    // closes over a stale `onHeaderCellWidthsChange`/`onHorizontalScroll`.
    const callbacksRef = useRef({ onHeaderCellWidthsChange, onHorizontalScroll });
    useEffect(() => {
        callbacksRef.current = { onHeaderCellWidthsChange, onHorizontalScroll };
    });

    // RECUR-27656 — sticky header clone support. Measures the real thead cells' rendered widths
    // (auto table layout stretches columns when few FYs are on a wide screen, so the nominal
    // min-widths aren't reliable) and syncs the clone's scroll position to this table's own.
    // Both are no-ops when the caller doesn't opt in (AuditedStatementSection only wires these once
    // a statement is pinned).
    const measure = () => {
        const row = theadRowRef.current;
        if (row) {
            const widths = Array.from(row.children).map((th) => (th as HTMLElement).getBoundingClientRect().width);
            callbacksRef.current.onHeaderCellWidthsChange?.(widths);
        }
        if (containerRef.current) callbacksRef.current.onHorizontalScroll?.(containerRef.current.scrollLeft);
    };

    useEffect(() => {
        const { onHeaderCellWidthsChange: latestWidthsChange, onHorizontalScroll: latestScroll } = callbacksRef.current;
        if (!hasData || !tableVisible || (!latestWidthsChange && !latestScroll)) return undefined;
        const raf = requestAnimationFrame(measure);
        let ro: ResizeObserver | undefined;
        if (latestWidthsChange && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(measure);
            Array.from(theadRowRef.current?.children ?? []).forEach((th) => ro?.observe(th));
        }
        return () => {
            cancelAnimationFrame(raf);
            ro?.disconnect();
        };
    }, [hasData, tableVisible, valueHeadersLength, unit]);

    const handleScroll = (scrollLeft: number) => onHorizontalScroll?.(scrollLeft);

    return { containerRef, theadRowRef, handleScroll };
}
